import { rulesEngineGet, rulesEngineRequest } from './rulesEngineClient';
import { getAccessManagementPermissionsUrl, getAccessManagementRolesUrl } from './rulesEngineEndpoints';

const asText = (value) => {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'string') {
    return value.trim();
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  return '';
};

const asIdList = (value) => (Array.isArray(value) ? value.map((id) => asText(id)).filter(Boolean) : []);

export const normalizeAmsRole = (role, index = 0) => ({
  id: asText(role?.id || role?._id) || `ams-role-${index}`,
  name: asText(role?.name),
  organizationId: asText(role?.organization_id),
  isDefault: Boolean(role?.is_default),
  active: role?.active !== false,
  permissionIds: asIdList(role?.permission_ids),
});

export const normalizeAmsPermission = (permission, index = 0) => {
  const resource = asText(permission?.resource);
  const action = asText(permission?.action);

  return {
    id: asText(permission?.id || permission?._id) || `ams-permission-${index}`,
    resource,
    action,
    key: `${resource}.${action}`,
  };
};

const buildAmsRolePayload = (payload = {}) => ({
  ...(payload.name ? { name: payload.name } : {}),
  ...(payload.organizationId ? { organization_id: payload.organizationId } : {}),
  ...(payload.active !== undefined ? { active: payload.active } : {}),
  ...(payload.permissionIds ? { permission_ids: payload.permissionIds } : {}),
});

const extractAmsCollection = (response, key) => {
  if (Array.isArray(response)) {
    return response;
  }

  return Array.isArray(response?.[key]) ? response[key] : [];
};

export const getAccessManagementRoles = async () => {
  const response = await rulesEngineGet(getAccessManagementRolesUrl());
  const roles = extractAmsCollection(response, 'roles');

  return roles.map((role, index) => normalizeAmsRole(role, index));
};

export const getAccessManagementPermissions = async () => {
  const response = await rulesEngineGet(getAccessManagementPermissionsUrl());
  const permissions = extractAmsCollection(response, 'permissions');

  return permissions.map((permission, index) => normalizeAmsPermission(permission, index));
};

export const createAccessManagementRole = async (payload = {}) => {
  const response = await rulesEngineRequest({
    method: 'post',
    url: getAccessManagementRolesUrl(),
    data: { role: buildAmsRolePayload(payload) },
  });

  return normalizeAmsRole(response?.data ?? response);
};

export const updateAccessManagementRole = async (roleId, payload = {}) => {
  if (!roleId) {
    throw new Error('roleId is required to update AMS role');
  }

  const response = await rulesEngineRequest({
    method: 'patch',
    url: getAccessManagementRolesUrl(roleId),
    data: { role: buildAmsRolePayload(payload) },
  });

  return normalizeAmsRole(response?.data ?? response);
};

export const deleteAccessManagementRole = async (roleId, reason) => {
  if (!roleId) {
    throw new Error('roleId is required to delete AMS role');
  }

  await rulesEngineRequest({
    method: 'delete',
    url: getAccessManagementRolesUrl(roleId),
    data: reason ? { reason } : {},
  });
};
